import { getAPIOperation } from './operations';
import type { OperationId, OperationSpec } from './types';

/**
 * Split the input params into path params and query params for an API operation.
 */
export function splitAPIOperationParams<Id extends OperationId>(input: {
    operationId: Id;
    params?: OperationSpec<Id>['RequestParams'] & OperationSpec<Id>['RequestQuery'];
}): {
    pathParams: OperationSpec<Id>['RequestParams'];
    queryParams: OperationSpec<Id>['RequestQuery'];
} {
    const { operationId, params } = input;
    const apiOperation = getAPIOperation(operationId);

    const pathParams: OperationSpec<Id>['RequestParams'] = {};
    const queryParams: OperationSpec<Id>['RequestQuery'] = {};

    for (const paramName in params) {
        const param = params[paramName as keyof typeof params];
        if (param === undefined) {
            continue;
        }

        if (apiOperation.route.includes(`{${paramName}}`)) {
            // @ts-ignore
            pathParams[paramName] = param;
        } else {
            // @ts-ignore
            queryParams[paramName] = param;
        }
    }

    return { pathParams, queryParams };
}

/**
 * Return the URL path for an API operation.
 */
export function getAPIOperationPath<Id extends OperationId>(input: {
    operationId: Id;
    params?: OperationSpec<Id>['RequestParams'];
    query?: OperationSpec<Id>['RequestQuery'];
    body?: OperationSpec<Id>['RequestBody'];
}): string {
    const { operationId, params } = input;
    const apiOperation = getAPIOperation(operationId);

    const operationPath = apiOperation.route.replace(/{(\w+)}/g, (_match, paramName) => {
        if (!params) {
            throw new Error('Input params are missing from input when the URL requires params');
        }

        // @ts-ignore
        const paramValue = params[paramName];
        if (!paramValue) {
            throw new Error(
                `URL param "${paramName}" value missing from input. Operation Endpoint: ${apiOperation.route}`
            );
        }
        return paramValue;
    });

    return operationPath;
}
